import React, { useState } from 'react';
import { useParams } from 'react-router';
import { useGetAddresses, useDeleteAddress, useCreateAddress, useUpdateAddress } from '@/services/api/hooks/address';
import { AddressDto, AddressRequest } from '@/services/api/contracts/address';
import { Button } from '@/components/form/Button';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Modal } from '@/components/modal/Modal';
import { AddressFormModal } from '@/components/form/fields/address/AddressFormModal';
import { toast } from 'sonner';

export const SavedAddressesPage: React.FC = () => {
  const { partnerId, customerId } = useParams<{ partnerId: string; customerId: string }>();
  const { mspId, companyId } = useParams<{ mspId?: string; companyId?: string }>();
  
  const effectiveMspId = partnerId || mspId;
  const effectiveCustomerId = customerId || companyId;

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<AddressDto | null>(null);

  const [addresses, query] = useGetAddresses(effectiveMspId, effectiveCustomerId);
  const isLoading = query.isLoading;
  const deleteAddress = useDeleteAddress();
  const createAddress = useCreateAddress();
  const updateAddress = useUpdateAddress();

  const handleOpenEdit = (address: AddressDto) => {
    setSelectedAddress(address);
    setIsEditModalOpen(true);
  };

  const handleOpenDelete = (address: AddressDto) => {
    setSelectedAddress(address);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAddress || !effectiveMspId) return;
    try {
      await deleteAddress.mutateAsync({
        id: selectedAddress.id,
        mspId: effectiveMspId,
        customerId: effectiveCustomerId
      });
      toast.success('Address deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedAddress(null);
    } catch (error: any) {
      toast.error('Failed to delete address');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Saved Addresses</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your delivery and billing addresses</p>
        </div>
        <Button onClick={() => setIsAddModalOpen(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add New Address
        </Button>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-lg shadow border border-slate-200 dark:border-slate-800 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-500">Loading addresses...</div>
        ) : !addresses || addresses.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No saved addresses found. Click 'Add New Address' to create one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-700 uppercase bg-slate-50 dark:bg-slate-800 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-6 py-4 font-medium">Name</th>
                  <th className="px-6 py-4 font-medium">Address</th>
                  <th className="px-6 py-4 font-medium">City / State</th>
                  <th className="px-6 py-4 font-medium">Postcode</th>
                  <th className="px-6 py-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {addresses.map((address: AddressDto) => (
                  <tr key={address.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-6 py-4 font-medium text-slate-900 dark:text-white">{address.name}</td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                      {address.line1}
                      {address.line2 && <><br /><span className="text-slate-500">{address.line2}</span></>}
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                      {address.city}
                      {address.state && <>, {address.state}</>}
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">{address.postcode}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button type="button" onClick={() => handleOpenEdit(address)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button type="button" onClick={() => handleOpenDelete(address)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} open={() => {}} close={() => setIsDeleteModalOpen(false)} className="max-w-md bg-white p-6 rounded-lg">
        <Modal.Controls close={() => setIsDeleteModalOpen(false)} isOpen={isDeleteModalOpen} open={() => {}}>
          <h2 className="text-lg font-semibold">Delete Address</h2>
        </Modal.Controls>
        <div className="py-4 text-slate-600 dark:text-slate-300">
          Are you sure you want to delete the address <strong>{selectedAddress?.name}</strong>? This action cannot be undone.
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
          <Button type="button" onClick={handleDeleteConfirm}>Delete Address</Button>
        </div>
      </Modal>

      {/* Add Address Modal */}
      {effectiveMspId && (
        <AddressFormModal 
          isOpen={isAddModalOpen} 
          mode="add"
          onClose={() => setIsAddModalOpen(false)} 
          onSave={async (payload) => {
            await createAddress.mutateAsync({ ...payload, mspId: effectiveMspId, customerId: effectiveCustomerId });
            toast.success('Address created successfully');
            setIsAddModalOpen(false);
          }}
        />
      )}

      {/* Edit Address Modal */}
      {isEditModalOpen && selectedAddress && effectiveMspId && (
        <AddressFormModal
          isOpen={isEditModalOpen}
          mode="edit"
          initialData={selectedAddress}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedAddress(null);
          }}
          onSave={async (payload) => {
            await updateAddress.mutateAsync({ ...payload, id: selectedAddress.id, mspId: effectiveMspId, customerId: effectiveCustomerId });
            toast.success('Address updated successfully');
            setIsEditModalOpen(false);
            setSelectedAddress(null);
          }}
        />
      )}
    </div>
  );
};


